"use client"

import type { ReactNode } from "react"
import { createContext, useContext, useMemo } from "react"
import { useQuery } from "@tanstack/react-query"
import type { SessionState, UserSession } from "@/types/session"

type SessionContextType = {
  session: SessionState
  loading: boolean
  isAuthenticated: boolean
  isGuest: boolean
  refreshSession: () => Promise<void>
}

const SessionContext = createContext<SessionContextType | undefined>(undefined)

const normalizeSession = (data?: UserSession | null): SessionState => ({
  user: data ?? null,
})

async function fetchSession(): Promise<UserSession | null> {
  try {
    const res = await fetch("/api/auth/me", {
      method: "GET",
      cache: "no-store",
      credentials: "include",
    })

    if (!res.ok) {
      // Log non-200 responses for debugging — this is the critical diagnostic
      console.debug(`[SessionProvider] /api/auth/me returned ${res.status}`)
      return null
    }

    const data: { user?: UserSession | null } = await res.json()
    return data.user ?? null
  } catch (error) {
    console.error("[SessionProvider] Failed to fetch session:", error)
    return null
  }
}

export const SessionProvider = ({
  children,
  initialSession = null,
}: {
  children: ReactNode
  initialSession?: UserSession | null
}) => {
  const { data, isFetching, refetch } = useQuery<UserSession | null, unknown>({
    queryKey: ["session"],
    queryFn: fetchSession,
    // Only use initialData if we actually have a session — don't cache null as "valid" data
    initialData: initialSession ?? undefined,
    // 5s staleTime is enough to prevent excessive re-fetching while being responsive
    // to login/logout transitions. 30s was too long — stale null session persisted
    staleTime: 5_000,
    refetchOnWindowFocus: true,
    // Refetch on mount to catch cookie changes (e.g., login in another tab)
    refetchOnMount: true,
  })

  // Ensure safe derivation of state
  const sessionUser = data ?? initialSession ?? null
  const session = useMemo(() => normalizeSession(sessionUser), [sessionUser])

  const value = useMemo<SessionContextType>(
    () => ({
      session,
      loading: isFetching,
      isAuthenticated: !!session.user,
      isGuest: !session.user && !isFetching,
      refreshSession: async () => {
        await refetch().then(() => undefined)
      },
    }),
    [session, isFetching, refetch]
  )

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  )
}

export const useSession = (): SessionContextType => {
  const context = useContext(SessionContext)
  if (!context) {
    // Return a safe "guest" session if used outside a provider (e.g., on static public pages)
    return {
      session: { user: null },
      loading: false,
      isAuthenticated: false,
      isGuest: true,
      refreshSession: async () => {},
    }
  }
  return context
}

export default SessionContext